import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import qs from 'qs';

import { FormBoolean, PageSection, Loading } from 'adc-ui-components';

import { formatAccountNumber, sortCurrentAccountFirst, getBillPayAccounts } from '../helpers/account';
import { isAptEnabled } from '../helpers/auth';
import Card from '../components/Card';
import { switchAccount } from '../actions/auth';
import { withQuery, getRouterBasename } from '../helpers/routes';

const AccountSwitcher = ({ location: { search = '' } = {} }) => {
  // useSelector to get redux state
  const { auth } = useSelector(state => ({
    auth: state.auth,
  }));

  const { accountIndex: currentAccountIndex, macaroon: { accounts } = {} } = auth;
  const currentAccount = accounts[currentAccountIndex];
  const accountsWithBillPay = getBillPayAccounts(accounts);

  const [index, updateAccountIndex] = useState(currentAccountIndex);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();

  const onFormSubmit = async (e) => {
    e.preventDefault();
    // Set a loading state here once an account is selected because switchAccount will call getAuth
    // with showLoading === false to prevent the page header from flickering while macaroon resolves
    setLoading(true);
    await dispatch(switchAccount(index));

    // Get the continue query param where the flow should resume
    const { continueUrl } = qs.parse(search.slice(1));
    // Set query param "sa" short for "selected account"
    const redirectUrl = withQuery(continueUrl, { sa: true });

    // Use server-side navigation when APT is disabled to ensure a full page reload,
    // which re-initializes the app state with the newly selected account.
    // Prepend basePath + /devices because window.location.assign is not React Router-aware
    // and does not automatically include the Router basename.
    if (!isAptEnabled()) {
      window.location.assign(`${getRouterBasename()}${redirectUrl}`);
      return;
    }

    history.push(redirectUrl);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <PageSection>
      <form
        noValidate
        onSubmit={onFormSubmit}
      >
        <div className="hgroup hgroup--inline ">
          <h2>Switch accounts</h2>
          <a href="https://idm.xfinity.com/myaccount/manage-accounts?execution=e1s1">Manage Accounts</a>
        </div>

        <div className="card-group">
          {sortCurrentAccountFirst(accountsWithBillPay, currentAccount.accountNumber).map(({
            accountNumber,
            alias,
            guid,
            accountIndex,
            serviceAddress,
          }) => (
            <div className="card-group__item" key={guid}>
              <Card>
                <FormBoolean>
                  <input
                    id={`account${accountIndex}`}
                    value={`account${accountIndex}`}
                    type="radio"
                    name="account"
                    defaultChecked={accountNumber === currentAccount.accountNumber}
                    onChange={() => updateAccountIndex(accountIndex)}
                  />
                  <label htmlFor={`account${accountIndex}`} className="form-control__label">
                    <span>
                      <span style={{ marginBottom: '8px', display: 'block' }}>
                        {`Account ${formatAccountNumber(accountNumber)}`}
                      </span>
                      {alias && <span className="color--text-color">{`'${alias}'`}</span>}
                      {!alias && <span className="color--text-color" data-notranslate>{serviceAddress.addressLine1}</span>}
                      {accountNumber === currentAccount.accountNumber && (
                        <span className="ml12 mb0 badge">Current Account</span>
                      )}
                    </span>
                  </label>
                </FormBoolean>
              </Card>
            </div>
          ))}
        </div>

        <div className="action action--right">
          <div className="action__item">
            <button type="submit" className="button button--primary">Confirm</button>
          </div>
          <div className="action__item">
            <a className="button" href="/">Cancel</a>
          </div>
        </div>
      </form>
    </PageSection>
  );
};

export default AccountSwitcher;
